import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { CheckCircle2, Zap, BarChart3, Image as ImageIcon, Video, MessageSquare, TrendingUp, Clock, Globe, Target, Bot, Sparkles, ArrowRight, Play, Mail, Phone, MapPin, Shield, Cpu, Users, ChevronDown, ChevronUp, Search, Brain, Layers } from 'lucide-react';

// Homepage FAQs for AEO
const HomepageFAQs = [
  {
    question: "What is AutomateSEO.ai?",
    answer: "AutomateSEO.ai is India's #1 AI-powered SEO automation platform that handles complete SEO, AEO (Answer Engine Optimization), GEO (Generative Engine Optimization), local rankings, Google Business Profile, social media, and content creation automatically. It saves businesses 40+ hours weekly and reduces marketing costs by 70%."
  },
  {
    question: "How does SEO automation work?",
    answer: "SEO automation uses AI agents to perform tasks like keyword research, on-page optimization, content creation, backlink building, and performance tracking automatically. AutomateSEO.ai's AI agents work 24/7, analyzing your website, generating optimized content, and posting to social media without manual intervention."
  },
  {
    question: "What is AEO (Answer Engine Optimization)?",
    answer: "AEO (Answer Engine Optimization) is the practice of optimizing content to appear in featured snippets, voice search results, and AI answer boxes. It focuses on providing direct, concise answers in 40-60 words that search engines can easily extract and display as featured answers."
  },
  {
    question: "What is GEO (Generative Engine Optimization)?",
    answer: "GEO (Generative Engine Optimization) is optimizing content for AI-powered search engines like ChatGPT, Google Gemini, and Perplexity. It ensures your brand appears in AI-generated responses by creating authoritative, entity-rich content that AI systems can understand and cite."
  },
  {
    question: "How much does AutomateSEO.ai cost?",
    answer: "AutomateSEO.ai offers 3 plans: Starter (₹2,999/month - AI-powered SEO automation, Google indexing, Core Web Vitals monitoring, AEO & GEO optimization, weekly reports), Pro (₹3,999/month - Everything in Starter plus social media automation, AI images & videos, blog articles), and Advanced (₹4,999/month - Everything in Pro plus high-quality backlinks, 10+ social posts weekly, 4 AI images & videos weekly). All plans include 18% GST."
  },
  {
    question: "Can I try AutomateSEO.ai for free?",
    answer: "Yes, AutomateSEO.ai offers a 3-day free trial with no credit card required. You get access to basic SEO features, 2 social media posts, basic SEO reports, and 5 AI-generated images to experience the platform before choosing a paid plan."
  },
  {
    question: "What platforms does AutomateSEO.ai support?",
    answer: "AutomateSEO.ai supports Instagram, Facebook, LinkedIn, Twitter/X, YouTube, and Google Business Profile for social media automation. For SEO, it integrates with Google Search Console, Google Analytics 4, and PageSpeed Insights for comprehensive optimization."
  },
  {
    question: "How is AutomateSEO.ai different from other SEO tools?",
    answer: "Unlike traditional SEO tools that only provide data, AutomateSEO.ai actually performs SEO tasks automatically. It generates content, posts to social media, builds backlinks, and optimizes your website 24/7 without manual work. It combines SEO, AEO, GEO, local SEO, and social media in one AI-powered platform."
  }
];

// Getting Started Steps
const GettingStartedSteps = [
  {
    name: "Sign Up for Free Trial",
    text: "Create your AutomateSEO.ai account with email. No credit card required for the 3-day free trial."
  },
  {
    name: "Choose Your Plan",
    text: "Select from Starter (₹2,999), Pro (₹3,999), or Advanced (₹4,999) based on your business needs."
  },
  {
    name: "Complete Onboarding",
    text: "Fill out the 8-step onboarding form with your business details, website URL, target keywords, and social media accounts."
  },
  {
    name: "Connect Your Accounts",
    text: "Link Google Search Console, Google Analytics, and social media platforms for full automation."
  },
  {
    name: "Let AI Work",
    text: "AI agents automatically analyze your website, generate content, optimize SEO, and post to social media weekly."
  },
  {
    name: "Review Weekly Reports",
    text: "Receive automated weekly reports via email showing SEO improvements, content created, and social media performance."
  }
];

const LandingPage = () => {
  const navigate = useNavigate();
  const [activeFeature, setActiveFeature] = useState(0);
  const [openFaq, setOpenFaq] = useState(null);

  const aiFeatures = [
    {
      icon: Bot,
      title: "AI-Powered SEO & Marketing Automation",
      description: "Experience the future of digital marketing. Our advanced AI systems work 24/7 to optimize your online presence with SEO, AEO, and GEO, eliminating manual tasks and maximizing results.",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: TrendingUp,
      title: "Enterprise-Grade SEO, AEO & GEO",
      description: "Achieve top search rankings with intelligent SEO engine. Weekly audits, featured snippet optimization (AEO), AI overview targeting (GEO), and automated optimizations ensure your brand dominates all search results.",
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: Globe,
      title: "Google My Business Excellence",
      description: "Dominate local search with AI-managed Google Business Profile. Automated review responses, strategic post scheduling, and continuous optimization drive more foot traffic and qualified leads to your business.",
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: MessageSquare,
      title: "Multi-Platform Social Media Mastery",
      description: "Scale your social presence exponentially. AI generates 5-10 high-engagement posts weekly, optimized for each platform's algorithm - Instagram, Facebook, LinkedIn, YouTube, and X (Twitter). Never miss a trending opportunity.",
      color: "from-orange-500 to-red-500"
    },
    {
      icon: Sparkles,
      title: "Professional AI Content Studio",
      description: "Transform your content marketing with enterprise-grade AI. From SEO-optimized 1500+ word blog articles to stunning DALL-E 3 visuals and cinematic RunwayML Gen-3 videos - all created automatically every single week.",
      color: "from-violet-500 to-purple-500"
    },
    {
      icon: Clock,
      title: "Reclaim 40+ Hours Every Week",
      description: "What requires a full marketing team 40+ hours weekly, our AI delivers in minutes. Redirect saved time to strategic initiatives, business development, and revenue-generating activities. Time is your most valuable asset.",
      color: "from-yellow-500 to-orange-500"
    }
  ];

  const benefits = [
    "⚡ 100% Automated - Zero Manual Effort Required",
    "🚀 10X Faster Results Than Traditional Agencies",
    "💎 Save ₹50,000+ Monthly on Marketing Costs",
    "📊 Data-Driven AI Insights & Optimization",
    "⏱️ 5-Minute Setup - Start Automating Today",
    "📈 Weekly Performance Reports via Email & WhatsApp"
  ];

  const automationFlow = [
    { step: "1. Connect", desc: "One-time account linking", icon: Zap },
    { step: "2. AI Analyzes", desc: "Deep business intelligence", icon: Bot },
    { step: "3. AI Creates", desc: "Content generation weekly", icon: Sparkles },
    { step: "4. Auto Posts", desc: "Multi-platform publishing", icon: Target },
    { step: "5. Reports", desc: "Actionable insights delivered", icon: BarChart3 }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Navigation */}
      <nav className="navbar px-6 py-4 sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b" role="navigation" aria-label="Main navigation">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer hover:opacity-90 transition-opacity" onClick={() => navigate('/')}>
            <img 
              src="/AutomateSEO-logo.png" 
              alt="AutomateSEO.ai - AI SEO Automation Platform Logo" 
              className="w-12 h-12 rounded-xl shadow-lg"
              width="48"
              height="48"
            />
            <span className="text-2xl font-bold heading bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">AutomateSEO.ai</span>
          </div>
          <div className="flex items-center space-x-4">
            <Button variant="ghost" onClick={() => navigate('/seo-guide')} className="hidden md:inline-flex">SEO Guide</Button>
            <Button variant="ghost" onClick={() => navigate('/tutorials')} className="hidden md:inline-flex">Tutorials</Button>
            <Button variant="ghost" onClick={() => navigate('/login')} className="hidden sm:inline-flex">Login</Button>
            <Button onClick={() => navigate('/register')} className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
              Start Free Trial
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="container mx-auto px-6 pt-20 pb-32 relative overflow-hidden" aria-label="Hero section">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl"></div>
        
        <div className="relative z-10 text-center max-w-5xl mx-auto fade-in-up">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-full text-sm font-medium text-blue-700 mb-6">
            <Sparkles className="w-4 h-4" aria-hidden="true" />
            <span>AutomateSEO.ai is India&apos;s 1st fully automated AI platform for SEO, AEO, GEO &amp; Social Media Automation</span>
          </div>
          
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-black heading mb-6 leading-tight">
            <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">Connect Once.</span>
            <br />
            <span className="text-gray-900">AI Does Everything.</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-600 mb-4 max-w-3xl mx-auto leading-relaxed">
            Your complete <span className="font-bold text-blue-600">SEO</span>, <span className="font-bold text-cyan-600">AEO</span> and <span className="font-bold text-teal-600">GEO</span>, <span className="font-bold text-purple-600">Local Rankings</span>, <span className="font-bold text-pink-600">Google Business</span>, <span className="font-bold text-orange-600">Social Media</span>, <span className="font-bold text-green-600">Content Creation</span>, and <span className="font-bold text-red-600">Analytics</span> — fully automated on <span className="font-black text-gray-900">AI Autopilot</span>
          </p>
          
          <p className="text-lg text-gray-500 mb-10">
            <span className="font-semibold text-gray-900">Zero Manual Work</span> • <span className="font-semibold text-gray-900">No Human Required</span> • <span className="font-semibold text-gray-900">Just Results</span>
          </p>

          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
            <Button 
              size="lg" 
              className="text-lg px-10 py-7 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-xl hover:shadow-2xl transition-all"
              onClick={() => navigate('/register')}
            >
              Start 3-Day Free Trial
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="text-lg px-10 py-7 border-2 hover:bg-gray-50"
              onClick={() => navigate('/demo')}
            >
              <Play className="mr-2 h-5 w-5" />
              Watch Interactive Demo
            </Button>
          </div>
          
          <p className="text-sm text-gray-500">Made in India 🇮🇳 • 3-Day Free Trial • No Credit Card Required</p>
        </div>
      </section>

      {/* Benefits Bar */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-600 py-4">
        <div className="container mx-auto px-6">
          <div className="flex flex-wrap justify-center items-center gap-8 text-white text-sm font-medium">
            {benefits.map((benefit, i) => (
              <div key={i} className="flex items-center gap-2 animate-fade-in" style={{animationDelay: `${i * 100}ms`}}>
                {benefit}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How AI Works */}
      <section className="container mx-auto px-6 py-24">
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold heading mb-4">How Enterprise AI Automation Works</h2>
          <p className="text-xl text-gray-600">Configure once. AI optimizes forever. Results guaranteed.</p>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-center gap-6 max-w-6xl mx-auto">
          {automationFlow.map((item, i) => {
            const Icon = item.icon;
            return (
              <React.Fragment key={i}>
                <div className="flex flex-col items-center text-center group hover:scale-105 transition-transform">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center mb-4 shadow-lg group-hover:shadow-xl transition-shadow">
                    <Icon className="w-10 h-10 text-white" />
                  </div>
                  <p className="font-bold text-lg mb-1">{item.step}</p>
                  <p className="text-sm text-gray-600 max-w-[140px]">{item.desc}</p>
                </div>
                {i < automationFlow.length - 1 && (
                  <ArrowRight className="hidden md:block w-8 h-8 text-gray-300" />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </section>

      {/* AI Features Grid */}
      <section className="bg-gradient-to-br from-gray-50 to-blue-50 py-24">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold heading mb-4">Enterprise-Grade AI Marketing Platform</h2>
            <p className="text-xl text-gray-600">Everything your business needs to dominate digital marketing - fully automated by advanced AI</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {aiFeatures.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <Card 
                  key={i}
                  className="card border-0 shadow-xl hover:shadow-2xl transition-all cursor-pointer group"
                  onMouseEnter={() => setActiveFeature(i)}
                >
                  <CardHeader>
                    <div className={`w-14 h-14 bg-gradient-to-br ${feature.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <CardTitle className="text-xl">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base leading-relaxed">{feature.description}</CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* What AI Creates */}
      <section className="container mx-auto px-6 py-24">
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold heading mb-4">Professional Content, Created Weekly by AI</h2>
          <p className="text-xl text-gray-600">Enterprise-quality content automatically generated, optimized, and distributed across all channels</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          <Card className="card text-center">
            <CardHeader>
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="w-8 h-8 text-white" />
              </div>
              <CardTitle className="text-4xl font-bold text-blue-600">5-10</CardTitle>
              <CardDescription className="text-base mt-2">High-Engagement<br/>Social Media Posts</CardDescription>
            </CardHeader>
          </Card>

          <Card className="card text-center">
            <CardHeader>
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <ImageIcon className="w-8 h-8 text-white" />
              </div>
              <CardTitle className="text-4xl font-bold text-purple-600">2</CardTitle>
              <CardDescription className="text-base mt-2">Professional Marketing<br/>Images (DALL-E 3)</CardDescription>
            </CardHeader>
          </Card>

          <Card className="card text-center">
            <CardHeader>
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Video className="w-8 h-8 text-white" />
              </div>
              <CardTitle className="text-4xl font-bold text-orange-600">2</CardTitle>
              <CardDescription className="text-base mt-2">Cinematic Video Reels<br/>(RunwayML Gen-3)</CardDescription>
            </CardHeader>
          </Card>

          <Card className="card text-center">
            <CardHeader>
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="w-8 h-8 text-white" />
              </div>
              <CardTitle className="text-4xl font-bold text-green-600">1</CardTitle>
              <CardDescription className="text-base mt-2">SEO-Optimized Blog<br/>Article (1500+ words)</CardDescription>
            </CardHeader>
          </Card>
        </div>
      </section>

      {/* Social Proof */}
      <section className="bg-gradient-to-br from-blue-600 to-purple-600 py-24 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-5xl font-bold heading mb-8">Trusted by 1,000+ Growing Businesses</h2>
          <p className="text-xl opacity-90 mb-12">Join India&apos;s fastest-growing AI marketing automation platform</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 max-w-5xl mx-auto mt-16">
            <div>
              <p className="text-6xl font-bold mb-2">40+</p>
              <p className="text-xl opacity-90">Hours Saved Weekly</p>
              <p className="text-sm opacity-75 mt-2">Per business average</p>
            </div>
            <div>
              <p className="text-6xl font-bold mb-2">300%</p>
              <p className="text-xl opacity-90">Average ROI Increase</p>
              <p className="text-sm opacity-75 mt-2">Within 90 days</p>
            </div>
            <div>
              <p className="text-6xl font-bold mb-2">₹50K+</p>
              <p className="text-xl opacity-90">Monthly Cost Savings</p>
              <p className="text-sm opacity-75 mt-2">Compared to agencies</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="container mx-auto px-6 py-24">
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold heading mb-4">Transparent, Value-Driven Pricing</h2>
          <p className="text-xl text-gray-600">Start with 3-day free trial • No credit card required • Cancel anytime</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
          
          {/* Free Trial Plan */}
          <Card className="card border-2 hover:border-blue-600 hover:shadow-2xl transition-all">
            <CardHeader>
              <CardTitle className="text-2xl">Free Trial</CardTitle>
              <div className="text-5xl font-bold mt-4">
                ₹0<span className="text-lg text-gray-500">/3 days</span>
              </div>
              <CardDescription className="mt-2">Perfect to test all features</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Access to all Starter features</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>3-day full platform access</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>No credit card required</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Cancel anytime</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Email & WhatsApp support</span>
                </li>
              </ul>
              <Button className="w-full mt-6 bg-gradient-to-r from-blue-600 to-purple-600" onClick={() => navigate('/register')}>Start Free Trial</Button>
            </CardContent>
          </Card>

          {/* Starter Plan */}
          <Card className="card border-2 hover:border-blue-600 hover:shadow-2xl transition-all">
            <CardHeader>
              <CardTitle className="text-2xl">Starter</CardTitle>
              <div className="text-5xl font-bold mt-4">
                ₹2,999<span className="text-lg text-gray-500">/month</span>
              </div>
              <CardDescription className="mt-2">+ GST | Perfect for small businesses & startups</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>AI-powered SEO automation (Search Console + GA4)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Automatic Google indexing & sitemap submission</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Core Web Vitals & PageSpeed monitoring</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Answer Engine Optimization (AEO)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>GEO / AI-search visibility optimization</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Weekly automated SEO reports</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Email & WhatsApp delivery</span>
                </li>
              </ul>
              <Button className="w-full mt-6 bg-gradient-to-r from-blue-600 to-purple-600" onClick={() => navigate('/register')}>Start Free Trial</Button>
            </CardContent>
          </Card>

          <Card className="card border-4 border-blue-600 relative shadow-2xl transform scale-105">
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
              🔥 MOST POPULAR
            </div>
            <CardHeader className="pt-8">
              <CardTitle className="text-2xl">Pro</CardTitle>
              <div className="text-5xl font-bold mt-4">
                ₹3,999<span className="text-lg text-gray-500">/month</span>
              </div>
              <CardDescription className="mt-2">+ GST | Best for growing brands & e-commerce</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="font-medium">Everything in Starter, plus:</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>2 professional AI images weekly (DALL-E 3)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>2 cinematic AI videos weekly (RunwayML Gen-3)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>1 SEO-optimized blog article (1500+ words)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Advanced analytics & insights dashboard</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Priority 24/7 support via chat & email</span>
                </li>
              </ul>
              <Button className="w-full mt-6 bg-gradient-to-r from-blue-600 to-purple-600" onClick={() => navigate('/register')}>Start Free Trial</Button>
            </CardContent>
          </Card>

          {/* Advanced Plan */}
          <Card className="card border-2 hover:border-blue-600 hover:shadow-2xl transition-all">
            <CardHeader>
              <CardTitle className="text-2xl">Advanced</CardTitle>
              <div className="text-5xl font-bold mt-4">
                ₹4,999<span className="text-lg text-gray-500">/month</span>
              </div>
              <CardDescription className="mt-2">+ GST | For enterprises & high-growth brands</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="font-medium">Everything in Pro, plus:</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>High-quality backlink creation</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>AI search visibility boost</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>10+ AI-generated social posts weekly</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>4 professional AI images weekly</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>4 cinematic AI videos weekly</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>2 SEO-optimized blog articles</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Dedicated account manager</span>
                </li>
              </ul>
              <Button className="w-full mt-6 bg-gradient-to-r from-blue-600 to-purple-600" onClick={() => navigate('/register')}>Get Started</Button>
            </CardContent>
          </Card>

        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-600 py-20" aria-label="Call to action">
        <div className="container mx-auto px-6 text-center text-white">
          <h2 className="text-5xl font-bold heading mb-6">Ready to Transform Your Marketing?</h2>
          <p className="text-2xl mb-10 opacity-90">Join 1,000+ businesses experiencing the power of AI automation</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button 
              size="lg" 
              className="text-lg px-10 py-7 bg-white text-blue-600 hover:bg-gray-100"
              onClick={() => navigate('/register')}
            >
              Start 3-Day Free Trial
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="text-lg px-10 py-7 border-2 border-white text-white hover:bg-white/10"
              onClick={() => navigate('/demo')}
            >
              <Play className="mr-2 h-5 w-5" />
              Watch Demo
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section for AEO - Answer Engine Optimization */}
      <section className="py-20 bg-white" id="faq" aria-label="Frequently Asked Questions">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 border border-purple-200 rounded-full text-sm font-medium text-purple-700 mb-4">
              <Brain className="w-4 h-4" />
              <span>AEO Optimized - Featured Snippet Ready</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold heading mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Get answers to common questions about SEO, AEO, GEO automation and how AutomateSEO.ai can help your business grow.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {HomepageFAQs.map((faq, index) => (
              <div 
                key={index} 
                className="border-b border-gray-200 last:border-b-0"
                itemScope 
                itemProp="mainEntity" 
                itemType="https://schema.org/Question"
              >
                <button
                  className="w-full py-6 flex justify-between items-center text-left hover:bg-gray-50 transition-colors px-4 rounded-lg"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  aria-expanded={openFaq === index}
                  aria-controls={`faq-answer-${index}`}
                >
                  <h3 className="text-lg font-semibold text-gray-900 pr-8" itemProp="name">
                    {faq.question}
                  </h3>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-purple-600 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                <div 
                  id={`faq-answer-${index}`}
                  className={`overflow-hidden transition-all duration-300 ${openFaq === index ? 'max-h-96 pb-6' : 'max-h-0'}`}
                  itemScope 
                  itemProp="acceptedAnswer" 
                  itemType="https://schema.org/Answer"
                >
                  <p className="text-gray-600 px-4 leading-relaxed faq-answer" itemProp="text">
                    {faq.answer}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Quick Answer Box for AEO */}
          <div className="mt-16 max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 border border-blue-100">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Search className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">What is AutomateSEO.ai in 60 Words?</h3>
                  <p className="text-gray-700 leading-relaxed hero-description">
                    AutomateSEO.ai is India&apos;s #1 AI-powered marketing automation platform. It fully automates SEO (search engine optimization), AEO (answer engine optimization), GEO (generative engine optimization), local rankings, Google Business Profile, social media posting, and content creation. Businesses save 40+ hours weekly and reduce marketing costs by 70%. Plans start at ₹2,999/month with a free 3-day trial.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section for GEO */}
      <section className="py-20 bg-gray-50" id="how-it-works" aria-label="How AutomateSEO works">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-full text-sm font-medium text-green-700 mb-4">
              <Layers className="w-4 h-4" />
              <span>GEO Optimized - AI Overview Ready</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold heading mb-4">How to Get Started with AutomateSEO.ai</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Set up your AI-powered SEO automation in just 10 minutes. Follow these 6 simple steps.
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {GettingStartedSteps.map((step, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow border border-gray-100"
                  itemScope
                  itemType="https://schema.org/HowToStep"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center text-white font-bold text-xl mb-4">
                    {index + 1}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2" itemProp="name">{step.name}</h3>
                  <p className="text-gray-600" itemProp="text">{step.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* AI Summary Box for GEO */}
          <div className="mt-16 max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Bot className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">AI Summary: AutomateSEO.ai Capabilities</h3>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Core Services:</strong> SEO Automation, AEO (Answer Engine Optimization), GEO (Generative Engine Optimization), Local SEO, Google Business Profile Management, Social Media Automation (Instagram, Facebook, LinkedIn, Twitter/X, YouTube), AI Content Generation (Blogs, Images with DALL-E 3, Videos with RunwayML).
                  </p>
                  <p className="text-gray-700 leading-relaxed mt-2">
                    <strong>Key Benefits:</strong> 40+ hours saved weekly, 70% cost reduction, 24/7 AI automation, weekly performance reports, enterprise-grade features for all business sizes.
                  </p>
                  <p className="text-gray-700 leading-relaxed mt-2">
                    <strong>Pricing:</strong> Starter (₹2,999/month), Pro (₹3,999/month), Advanced (₹4,999/month). Free 3-day trial available.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer with Tabs */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="container mx-auto px-6">
          <Tabs defaultValue="company" className="w-full">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-12 gap-8">
              <div className="flex-shrink-0">
                <div className="flex items-center space-x-3 mb-4">
                  <img 
                    src="/AutomateSEO-logo.png" 
                    alt="AutomateSEO.ai" 
                    className="w-12 h-12 rounded-xl shadow-lg"
                  />
                  <span className="text-2xl font-bold heading">AutomateSEO.ai</span>
                </div>
                <p className="text-gray-400 max-w-sm">Enterprise-grade AI marketing automation platform. Made in India with ❤️</p>
              </div>
              
              <TabsList className="bg-gray-800 p-1 flex-wrap">
                <TabsTrigger value="company" className="data-[state=active]:bg-blue-600">Company</TabsTrigger>
                <TabsTrigger value="product" className="data-[state=active]:bg-blue-600">Product</TabsTrigger>
                <TabsTrigger value="resources" className="data-[state=active]:bg-blue-600">Resources</TabsTrigger>
                <TabsTrigger value="legal" className="data-[state=active]:bg-blue-600">Legal</TabsTrigger>
                <TabsTrigger value="blog" className="data-[state=active]:bg-blue-600">Blog</TabsTrigger>
                <TabsTrigger value="contact" className="data-[state=active]:bg-blue-600">Contact</TabsTrigger>
              </TabsList>
            </div>

            <div className="border-t border-gray-800 pt-8">
              <TabsContent value="company" className="mt-0">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div>
                    <h4 className="font-semibold mb-4 flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      About Us
                    </h4>
                    <p className="text-gray-400 text-sm leading-relaxed mb-4">
                      AutomateSEO.ai is India&apos;s 1st fully automated AI platform for SEO, AEO, GEO &amp; Social Media Automation - serving 1,000+ businesses with enterprise-grade solutions.
                    </p>
                    <a href="/about" className="text-blue-400 hover:text-blue-300 transition text-sm font-semibold">Read More →</a>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-4">Our Mission</h4>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      Democratize enterprise marketing automation, making advanced AI accessible to businesses of all sizes across India and beyond.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-4">Made In India 🇮🇳</h4>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      Proudly developed and operated by Set India Business Private Limited, supporting the Digital India initiative.
                    </p>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="product" className="mt-0">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div>
                    <h4 className="font-semibold mb-3">Features</h4>
                    <ul className="space-y-2 text-gray-400 text-sm">
                      <li><a href="#" className="hover:text-white transition">SEO Automation</a></li>
                      <li><a href="#" className="hover:text-white transition">Local SEO</a></li>
                      <li><a href="#" className="hover:text-white transition">Social Media</a></li>
                      <li><a href="#" className="hover:text-white transition">AI Content Generation</a></li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3">Integrations</h4>
                    <ul className="space-y-2 text-gray-400 text-sm">
                      <li><a href="#" className="hover:text-white transition">Google My Business</a></li>
                      <li><a href="#" className="hover:text-white transition">Instagram</a></li>
                      <li><a href="#" className="hover:text-white transition">Facebook</a></li>
                      <li><a href="#" className="hover:text-white transition">LinkedIn</a></li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3">Pricing</h4>
                    <ul className="space-y-2 text-gray-400 text-sm">
                      <li><a href="#" className="hover:text-white transition">Starter Plan</a></li>
                      <li><a href="#" className="hover:text-white transition">Pro Plan</a></li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3">Platform</h4>
                    <ul className="space-y-2 text-gray-400 text-sm">
                      <li><a href="#" onClick={() => navigate('/demo')} className="hover:text-white transition">Interactive Demo</a></li>
                      <li><a href="#" className="hover:text-white transition">API Documentation</a></li>
                      <li><a href="#" className="hover:text-white transition">System Status</a></li>
                    </ul>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="resources" className="mt-0">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div>
                    <h4 className="font-semibold mb-3">Learn</h4>
                    <ul className="space-y-2 text-gray-400 text-sm">
                      <li><a href="/seo-guide" className="hover:text-white transition">SEO Guide</a></li>
                      <li><a href="/tutorials" className="hover:text-white transition">Tutorials</a></li>
                      <li><a href="/blog" className="hover:text-white transition">Blog</a></li>
                      <li><a href="/blog" className="hover:text-white transition">Case Studies</a></li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3">Support</h4>
                    <ul className="space-y-2 text-gray-400 text-sm">
                      <li><a href="/help-center" className="hover:text-white transition">Help Center</a></li>
                      <li><a href="/faq" className="hover:text-white transition">FAQs</a></li>
                      <li><a href="/help-center" className="hover:text-white transition">Video Tutorials</a></li>
                      <li><a href="/help-center" className="hover:text-white transition">Community Forum</a></li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3">Developers</h4>
                    <ul className="space-y-2 text-gray-400 text-sm">
                      <li><a href="#" className="hover:text-white transition">API Reference</a></li>
                      <li><a href="#" className="hover:text-white transition">SDK Downloads</a></li>
                      <li><a href="#" className="hover:text-white transition">Integration Guides</a></li>
                      <li><a href="#" className="hover:text-white transition">Changelog</a></li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3">Partners</h4>
                    <ul className="space-y-2 text-gray-400 text-sm">
                      <li><a href="#" className="hover:text-white transition">Agency Program</a></li>
                      <li><a href="#" className="hover:text-white transition">Affiliate Program</a></li>
                      <li><a href="#" className="hover:text-white transition">Technology Partners</a></li>
                    </ul>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="legal" className="mt-0">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div>
                    <h4 className="font-semibold mb-4 flex items-center gap-2">
                      <Shield className="w-5 h-5" />
                      Terms & Policies
                    </h4>
                    <ul className="space-y-2 text-gray-400 text-sm">
                      <li><a href="/terms" className="hover:text-white transition">Terms of Service</a></li>
                      <li><a href="/privacy" className="hover:text-white transition">Privacy Policy</a></li>
                      <li><a href="/cookie-policy" className="hover:text-white transition">Cookie Policy</a></li>
                      <li><a href="/acceptable-use" className="hover:text-white transition">Acceptable Use Policy</a></li>
                      <li><a href="/data-deletion" className="hover:text-white transition">Data Deletion Policy</a></li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-4">Compliance</h4>
                    <ul className="space-y-2 text-gray-400 text-sm">
                      <li><a href="#" className="hover:text-white transition">GDPR Compliance</a></li>
                      <li><a href="#" className="hover:text-white transition">Data Security</a></li>
                      <li><a href="#" className="hover:text-white transition">ISO Certifications</a></li>
                      <li><a href="#" className="hover:text-white transition">Security Whitepaper</a></li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-4">Business</h4>
                    <ul className="space-y-2 text-gray-400 text-sm">
                      <li><a href="/refund-policy" className="hover:text-white transition">Refund Policy</a></li>
                      <li><a href="/terms" className="hover:text-white transition">SLA Agreement</a></li>
                      <li><a href="/terms" className="hover:text-white transition">Trademark Guidelines</a></li>
                      <li><a href="/terms" className="hover:text-white transition">License Agreement</a></li>
                    </ul>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="blog" className="mt-0">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div>
                    <h4 className="font-semibold mb-3">SEO & Automation</h4>
                    <ul className="space-y-2 text-gray-400 text-sm">
                      <li><a href="/blog/what-is-seo-automation-complete-guide-2025" className="hover:text-white transition">What is SEO Automation?</a></li>
                      <li><a href="/blog/on-page-seo-optimization-checklist" className="hover:text-white transition">On-Page SEO Checklist</a></li>
                      <li><a href="/blog/keyword-research-strategy-2025" className="hover:text-white transition">Keyword Research Strategy</a></li>
                      <li><a href="/blog/backlink-building-strategies-2025" className="hover:text-white transition">Backlink Building Strategies</a></li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3">AEO & GEO</h4>
                    <ul className="space-y-2 text-gray-400 text-sm">
                      <li><a href="/blog/aeo-answer-engine-optimization-guide" className="hover:text-white transition">AEO Complete Guide</a></li>
                      <li><a href="/blog/geo-generative-engine-optimization-chatgpt-seo" className="hover:text-white transition">GEO for ChatGPT</a></li>
                      <li><a href="/blog/schema-markup-structured-data-guide" className="hover:text-white transition">Schema Markup Guide</a></li>
                      <li><a href="/blog/core-web-vitals-pagespeed-optimization-guide" className="hover:text-white transition">Core Web Vitals</a></li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3">Local & Social</h4>
                    <ul className="space-y-2 text-gray-400 text-sm">
                      <li><a href="/blog/local-seo-google-business-profile-optimization" className="hover:text-white transition">Local SEO & GBP</a></li>
                      <li><a href="/blog/social-media-automation-instagram-facebook-linkedin" className="hover:text-white transition">Social Media Automation</a></li>
                      <li><a href="/blog/ai-content-generation-blogs-images-videos" className="hover:text-white transition">AI Content Generation</a></li>
                      <li><a href="/blog/ecommerce-seo-product-page-optimization" className="hover:text-white transition">E-Commerce SEO</a></li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3">Analytics & Business</h4>
                    <ul className="space-y-2 text-gray-400 text-sm">
                      <li><a href="/blog/google-search-console-analytics-guide" className="hover:text-white transition">Search Console Guide</a></li>
                      <li><a href="/blog/seo-reporting-metrics-kpis-guide" className="hover:text-white transition">SEO Metrics & KPIs</a></li>
                      <li><a href="/blog/seo-for-startups-growth-strategy" className="hover:text-white transition">SEO for Startups</a></li>
                      <li><a href="/blog" className="hover:text-white transition font-semibold">View All Posts →</a></li>
                    </ul>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="contact" className="mt-0">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div>
                    <h4 className="font-semibold mb-4 flex items-center gap-2">
                      <Mail className="w-5 h-5" />
                      Email Support
                    </h4>
                    <p className="text-gray-400 text-sm mb-2">All Inquiries:</p>
                    <a href="mailto:support@automateseo.ai" className="text-blue-400 hover:text-blue-300 transition text-lg">support@automateseo.ai</a>
                    <p className="text-gray-400 text-sm mt-4">For sales, support, partnerships, and general inquiries</p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-4 flex items-center gap-2">
                      <Phone className="w-5 h-5" />
                      Phone Support
                    </h4>
                    <p className="text-gray-400 text-sm mb-2">Customer Support (24/7):</p>
                    <a href="tel:+919810535398" className="text-blue-400 hover:text-blue-300 transition text-lg font-medium">+91 9810535398</a>
                    <p className="text-gray-400 text-sm mt-4">Available: Monday - Sunday, 24/7</p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-4 flex items-center gap-2">
                      <MapPin className="w-5 h-5" />
                      Office Location
                    </h4>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      Set India Business Private Limited<br />
                      India<br />
                      Made in India 🇮🇳
                    </p>
                  </div>
                </div>
                <div className="mt-8 pt-8 border-t border-gray-800">
                  <h4 className="font-semibold mb-4">Connect With Us</h4>
                  <div className="flex gap-4">
                    <a 
                      href="https://www.facebook.com/share/14KUqcAf3Kx/?mibextid=wwXIfr" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="w-12 h-12 bg-gray-800 hover:bg-blue-600 rounded-full flex items-center justify-center transition-all hover:scale-110"
                      title="Facebook"
                    >
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                    </a>
                    <a 
                      href="https://www.instagram.com/automateseo.ai?igsh=bTl3YmVrcWN3dzYy&utm_source=qr" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="w-12 h-12 bg-gray-800 hover:bg-gradient-to-br hover:from-purple-600 hover:to-pink-600 rounded-full flex items-center justify-center transition-all hover:scale-110"
                      title="Instagram"
                    >
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      </svg>
                    </a>
                  </div>
                </div>
              </TabsContent>
            </div>
          </Tabs>

          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400">
            <p className="text-sm">© 2025 Set India Business Private Limited. All rights reserved.</p>
            <p className="text-xs mt-2 opacity-75">Powered by Enterprise AI • Made in India 🇮🇳</p>
            <div className="mt-4">
              <a 
                href="/admin/login" 
                className="text-xs text-gray-600 hover:text-gray-400 transition-colors"
              >
                Admin Access
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
